import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormGroupDirective, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationService, ConfirmEventType, MenuItem, MessageService } from 'primeng/api';
import { FileUpload } from 'primeng/fileupload';
import { AuthGateway } from 'src/app/application/gateway/AuthGateway';
import { DocumentServiceGateway } from 'src/app/application/gateway/DocumentServiceGateway';
import { ExportOfferDocumentGateway } from 'src/app/application/gateway/ExportOfferDocumentGateway';
import { ExportOfferGateway } from 'src/app/application/gateway/ExportOfferGateway';
import { ProductCertificateGateway } from 'src/app/application/gateway/ProductCertificateGateway';
import { ExportOfferInformation } from 'src/app/domain/models/ExportOffer';
import { ExportOfferDocument, NewExportOfferDocument } from 'src/app/domain/models/ExportOfferDocument';
import { ProductCertificate } from 'src/app/domain/models/ProductCertificate';
import { BasicUser } from 'src/app/domain/models/User';
import { Routes } from 'src/app/infrastructure/routes';
import { ErrorMessageUtil } from 'src/app/ui/util/error-message-util';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-user-product-certificates',
  templateUrl: './user-product-certificates.component.html',
  styleUrls: ['./user-product-certificates.component.css'],
  providers: [ConfirmationService]
})
export class UserProductCertificatesComponent implements OnInit {

  certificates: any[] = []
  certificatesAssigned: any[] = []
  selected: any | null = null
  loading: boolean = true

  profileUser: BasicUser | undefined

  breadcrumbMenuItems: MenuItem[] = [{ label: '' }]
  breadcrumbHome: MenuItem = { label: ''}

  documents: ExportOfferDocument[] = []
  documentsAssigned: ExportOfferDocument[] = []

  selectedDocument: ExportOfferDocument | undefined
  documentPdfFile: any
  documentDialogVisible: boolean = false
  submitDocument: boolean = false
  exportOffers: ExportOfferInformation[] = []
  selectedExportOfferId: number | undefined

  documentFormGroup: FormGroup = this._formBuilder.group({
    name: ['', Validators.required]
  })


  constructor(private _router: Router,
    private _formBuilder: FormBuilder,
    private _messageService: MessageService,
    private _confirmationService: ConfirmationService,
    private _authService: AuthGateway,
    private _productCertificateService: ProductCertificateGateway,
    private _exportOfferDocumentService: ExportOfferDocumentGateway,
    private _exportOfferService: ExportOfferGateway,
    private _documentService: DocumentServiceGateway,
    public translate: TranslateService) {

  }

  ngOnInit(): void {

    this.profileUser = this._authService.getUser()

    let breadcrumbStep1ItemLabel1 = 'Product certificates'
    let homeItemLabel = 'User panel'

    if(this.profileUser) {
      switch(this.profileUser.lang) {
        case 'en':
          breadcrumbStep1ItemLabel1 = 'Product certificates'
          homeItemLabel = 'User panel'
          break
        case 'es':
          breadcrumbStep1ItemLabel1 = 'Certificados de productos'
          homeItemLabel = 'Panel de usuario'
          break
      }
    }

    this.breadcrumbMenuItems = [{ label: breadcrumbStep1ItemLabel1, disabled: true }]
    this.breadcrumbHome = { label: homeItemLabel, routerLink: '/' + Routes.userPanelPage}

    this.getAll()
    this.getAllAssigned()
    this.getDocuments()
    this.getDocumentsAssigned()
  }

  getAll() {
    this.loading = true
    this._productCertificateService.getAllFreeByProfile().subscribe({
      next: (v) => {
        this.certificates = v
        this.loading = false
      },
      error: (e) => {
        this._messageService.add({ severity: 'error',
          summary: this.translate.instant('MessageService.Summary.FailedAction'),
          detail: this.translate.instant('MessageService.ProductCertificate.ManyRetrieveError') });
        console.log(e)
        this.loading = false
      },
      complete: () =>  {
        this.loading = false
        console.info('Product Certificates query successfully')
      }
    })
  }


  getAllAssigned() {
    this.loading = true
    this._productCertificateService.getAllAssignedByProfile().subscribe({
      next: (v) => {
        this.certificatesAssigned = v
        this.loading = false
      },
      error: (e) => {
        this._messageService.add({ severity: 'error',
          summary: this.translate.instant('MessageService.Summary.FailedAction'),
          detail: this.translate.instant('MessageService.ProductCertificate.ManyRetrieveError') });
        console.log(e)
        this.loading = false
      },
      complete: () =>  {
        this.loading = false
        console.info('Product Certificates query successfully')
      }
    })
  }

  goToCreateForm() {
    this._router.navigate([Routes.productCertificateForm, 0])
  }

  goToEditForm(id: number) {
    this._router.navigate([Routes.productCertificateForm, id])
  }

  onDelete(id: number) {
    this._confirmationService.confirm({
      message: this.translate.instant('MessageService.ProductCertificate.ConfirmDeleteDialogMessage'),
      header: this.translate.instant('MessageService.ProductCertificate.ConfirmDeleteDialogHeader'),
      icon: 'pi pi-info-circle',
      accept: () => {
        this._productCertificateService.delete(id).subscribe({
          next: (v) => {
            this.selected = null
            this._messageService.add({ severity: 'info',
              summary: this.translate.instant('MessageService.Summary.SuccessAction'),
              detail: this.translate.instant('MessageService.ProductCertificate.DeleteSuccess') })
            this.getAll()
          },
          error: (e) => {
            console.log(e)
            this._messageService.add({ severity: 'error',
              summary: this.translate.instant('MessageService.Summary.FailedAction'),
              detail: this.translate.instant('MessageService.ProductCertificate.DeleteError') });
          },
          complete: () => console.info('Product Certificate delete successfully')
        })
      },
      reject: (type: any) => {
          switch (type) {
              case ConfirmEventType.REJECT:
                //this._messageService.add({ severity: 'error', summary: 'Rejected', detail: 'Se canceló la eliminación' })
                break;
              case ConfirmEventType.CANCEL:
                //this._messageService.add({ severity: 'warn', summary: 'Cancelled', detail: 'Se canceló la eliminación' })
                break;
          }
      }
    })
  }

  getPdfUrl(certificate: ProductCertificate): string {
    if(certificate.pdfUrl) {
      let uri = certificate.pdfUrl.trim().replace(" ", "%20")
      return environment.apiUrl + uri
    }
    return ''
  }

  getDocuments() {
    this.documents =  []
    this.loading = true
    this._exportOfferDocumentService.getAllFreeByProfile().subscribe({
      next: (v) => {
        this.documents = v
        this.loading = false
      },
      error: (e) => {
        this._messageService.add({ severity: 'error',
          summary: this.translate.instant('MessageService.Summary.FailedAction'),
          detail: this.translate.instant('MessageService.ExportOfferDocument.ManyRetrieveError') });
        console.log(e)
        this.loading = false
      },
      complete: () =>  {
        this.loading = false
        console.info('Export Offer Documents query successfully')
      }
    })

  }

  getDocumentsAssigned() {
    this.documentsAssigned =  []
    this.loading = true
    this._exportOfferDocumentService.getAllAssignedByProfile().subscribe({
      next: (v) => {
        this.documentsAssigned = v
        this.loading = false
      },
      error: (e) => {
        this._messageService.add({ severity: 'error',
          summary: this.translate.instant('MessageService.Summary.FailedAction'),
          detail: this.translate.instant('MessageService.ExportOfferDocument.ManyRetrieveError') });
        this.loading = false
      },
      complete: () =>  {
        this.loading = false
      }
    })

  }

    getExportOfferDocument(exportOfferDocumentId: number) {
    this._documentService.getExportOfferDocument2(exportOfferDocumentId).subscribe((resultBlob: any) => {
      var downloadURL = window.URL.createObjectURL(resultBlob.body);
      window.open(downloadURL);})
  }

  onSelectDocumentFile(event: any) {
    this.documentPdfFile = event.files[0]
  }

  onClearDocumentFile(event: any) {
    this.documentPdfFile = null
  }

  getErrorMessage(formGroup: FormGroup, fieldName: string): string {
    return ErrorMessageUtil.getErrorMessage(formGroup, this.translate, fieldName)
  }

  saveDocument() {
    this.submitDocument = true
    if(this.documentFormGroup.valid) {
      this.loading = true
      if(this.selectedDocument) {
        this.selectedDocument.name = this.documentFormGroup.controls['name'].value
        this.selectedDocument.pdf = this.documentPdfFile ? this.documentPdfFile : this.selectedDocument.pdf

        this._exportOfferDocumentService.update(this.selectedDocument).subscribe({
          next: (v) => {
            this.selectedDocument = v
            this.getDocumentsAssigned()
            this._messageService.add({ severity: 'success',
              summary: this.translate.instant('MessageService.Summary.SuccessAction'),
              detail: this.translate.instant('MessageService.ExportOfferDocument.EditSuccess') })
            this.loading = false
            this.submitDocument = false
            this.documentDialogVisible = false
          },
          error: (e) => {
            this.loading = false
            this.submitDocument = false
            console.log(e)
            this._messageService.add({ severity: 'error',
              summary: this.translate.instant('MessageService.Summary.FailedAction'),
              detail: this.translate.instant('MessageService.ExportOfferDocument.EditError') });
          },
          complete: () =>  {
            this.loading = false
            this.submitDocument = false
            console.info('Document update successfully') }
        })
      }
      else {
        let newDocument: NewExportOfferDocument = {
          name: this.documentFormGroup.controls['name'].value,
          pdf: this.documentPdfFile,
        }

        this._exportOfferDocumentService.create(newDocument).subscribe({
          next: (v) => {
            this.selectedDocument = v
            this.getDocuments()
            this._messageService.add({ severity: 'success',
              summary: this.translate.instant('MessageService.Summary.SuccessAction'),
              detail: this.translate.instant('MessageService.ExportOfferDocument.CreateSuccess') })
            this.loading = false
            this.submitDocument = false
            this.documentDialogVisible = false
          },
          error: (e) => {
            this.loading = false
            this.submitDocument = false
            console.log(e)
            this._messageService.add({ severity: 'error',
              summary: this.translate.instant('MessageService.Summary.FailedAction'),
              detail: this.translate.instant('MessageService.ExportOfferDocument.CreateError') });
          },
          complete: () =>  {
            this.loading = false
            this.submitDocument = false
            console.info('Document create successfully')
          }
        })
      }
    }
    else {
      this.loading = false
    }
  }

    onDeleteDocument(document: ExportOfferDocument) {
    this._confirmationService.confirm({
      message: this.translate.instant('MessageService.ExportOfferDocument.ConfirmDeleteDialogMessage'),
      header: this.translate.instant('MessageService.ExportOfferDocument.ConfirmDeleteDialogHeader'),
      icon: 'pi pi-info-circle',
      accept: () => {
        this._exportOfferDocumentService.delete(document.id).subscribe({
          next: (v) => {
            this.selectedDocument = undefined
            this.getDocuments()
            this._messageService.add({ severity: 'info',
              summary: this.translate.instant('MessageService.Summary.SuccessAction'),
              detail: this.translate.instant('MessageService.ExportOfferDocument.DeleteSuccess') })

          },
          error: (e) => {
            console.log(e)
            this._messageService.add({ severity: 'error',
              summary: this.translate.instant('MessageService.Summary.FailedAction'),
              detail: this.translate.instant('MessageService.ExportOfferDocument.DeleteError') });
          },
          complete: () => console.info('Export Offer Document delete successfully')
        })
      },
      reject: (type: any) => {
          switch (type) {
              case ConfirmEventType.REJECT:
                //this._messageService.add({ severity: 'error', summary: 'Rejected', detail: 'Se canceló la eliminación' })
                break;
              case ConfirmEventType.CANCEL:
                //this._messageService.add({ severity: 'warn', summary: 'Cancelled', detail: 'Se canceló la eliminación' })
                break;
          }
      }
    })
  }

  openDocumentEditDialog(document: ExportOfferDocument, fileUploader: FileUpload) {
    this.selectedDocument = document
    this.documentFormGroup.controls['name'].setValue(this.selectedDocument.name)
    if(this.selectedDocument.pdfUrl) {
      let uri = this.selectedDocument.pdfUrl.trim().replace(" ", "%20")
      uri = environment.apiUrl + uri
      fetch(uri).then(r => r.blob()).then(blob => {
        if(blob && this.selectedDocument) {
          let filename = uri.substring(uri.lastIndexOf('/')+1)
          filename = filename.replace("%20", " ")
          let pdf = new File([blob], filename)
          fileUploader.clear()
          fileUploader.files = [pdf]
          this.documentPdfFile = pdf
        }
      })
    }
    this.documentDialogVisible = true
  }

  openDocumentCreateDialog(formDirective: FormGroupDirective, fileUploader: FileUpload) {
    fileUploader.clear()
    formDirective.resetForm()
    this.documentFormGroup.controls['name'].setValue('')
    this.documentPdfFile = null
    this.selectedDocument = undefined
    this.submitDocument = false
    this.documentDialogVisible = true
  }

  getOffers()
  {
      this.loading = true;
      this._exportOfferService.getAllExporterOffers().subscribe({
        next:  (v) => {
          this.loading = false;
          this.exportOffers = v
        },
        error: (e) => {
          this.loading = false;
        }
      })
  }

  getProductCertificateDocument(productCertificateId: number) {
    this._documentService.getProductCertificateDocument2(productCertificateId).subscribe((resultBlob: any) => {
      var downloadURL = window.URL.createObjectURL(resultBlob.body);
      window.open(downloadURL);})
  }

}
